import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API from '../../../context/API';

const PAYMENTS_BASE = 'payments';

export interface PaymentItem {
  merchant: string;
  amount: number | string;
  dueDate?: string;
  paymentDate?: string;
}

interface PaymentsApiResponse {
  data?: PaymentItem[];
}

interface PaymentsState {
  pending: PaymentItem[];
  cleared: PaymentItem[];
  upcoming: PaymentItem[];
  loading: boolean;
  error: string | null;
}

const initialState: PaymentsState = {
  pending: [],
  cleared: [],
  upcoming: [],
  loading: false,
  error: null,
};

const isDevelopmentServer =
  (typeof process !== 'undefined' &&
    (process.env.REACT_APP_ADMIN_URL ?? '').includes('dev.server.kiwikrave.com')) ||
  ((process.env.REACT_APP_ADMIN_URL || 'https://dev.server.kiwikrave.com').includes('dev.server.kiwikrave.com'));

export const fetchPayments = createAsyncThunk<
  { pending: PaymentItem[]; cleared: PaymentItem[]; upcoming: PaymentItem[] },
  void,
  { rejectValue: string }
>(
  'payments/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const [pendingRes, clearedRes, upcomingRes] = await Promise.all([
        API.get<PaymentsApiResponse>(`${PAYMENTS_BASE}/pending`),
        API.get<PaymentsApiResponse>(`${PAYMENTS_BASE}/cleared`),
        API.get<PaymentsApiResponse>(`${PAYMENTS_BASE}/upcoming`),
      ]);
      const pending = pendingRes.data?.data ?? [];
      const cleared = clearedRes.data?.data ?? [];
      const upcoming = upcomingRes.data?.data ?? [];
      return { pending, cleared, upcoming };
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      return rejectWithValue(
        typeof message === 'string' ? message : 'Failed to fetch payments'
      );
    }
  }
);

const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    clearPaymentsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPayments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading = false;
        state.pending = action.payload.pending;
        state.cleared = action.payload.cleared;
        state.upcoming = action.payload.upcoming;
        state.error = null;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading = false;
        if (!isDevelopmentServer) {
          state.pending = [];
          state.cleared = [];
          state.upcoming = [];
        }
        state.error = action.payload ?? 'Failed to fetch payments';
      });
  },
});

export const { clearPaymentsError } = paymentsSlice.actions;
export default paymentsSlice.reducer;
